import { IProductCatalog } from '../graphQL/commons'
import {
  ICatalogItem,
  IConditionAction,
  IConfig,
  IProduct,
  IPromotion,
} from '../interfaces'
import { ICatalogItem as ICatalogItemHeadless } from '../graphQL/queries/getCatalog/types'
import { sanitizeBundleExtra } from './sanitizeBundleExtra'
import { calculateOutOfStock } from './calculateOutOfStock'
import { sanitizeExtraPackages } from './sanitizeExtraPackages'
import sortByKey from './sort'

const productTransformers: IConditionAction<
  { source: IProductCatalog; result: IProduct; config?: IConfig },
  IProduct
>[] = [
  {
    condition: ({ source: { outOfStock } }) => outOfStock,
    transformAction: ({ result, source }) => ({
      ...result,
      outOfStock: calculateOutOfStock(source),
    }),
  },
  {
    condition: ({ source }) => !!source.jumpQty,
    transformAction: ({ result, source: { jumpQty } }) => ({
      ...result,
      step: jumpQty!,
    }),
  },
  {
    condition: ({ source }) => !!source.bundlesExtra,
    transformAction: ({
      result,
      source: { bundlesExtra, bundle },
      config,
    }) => ({
      ...result,
      bundlesExtra: bundlesExtra!.map((bundleExtra) =>
        sanitizeBundleExtra({
          bundleConfig: bundle,
          bundleExtra,
          config,
        }),
      ),
    }),
  },
  {
    condition: ({ source: { extraPackages } }) => !!extraPackages?.length,
    transformAction: ({ result, source: { extraPackages }, config }) => ({
      ...result,
      extraPackages: sanitizeExtraPackages(extraPackages!, config),
    }),
  },
  {
    condition: ({ source: { promotions } }) => !!promotions?.length,
    transformAction: ({ result, source: { promotions } }) => ({
      ...result,
      promotions: sortByKey<IPromotion>(promotions || [], 'priority'),
    }),
  },
  {
    condition: ({ source: { layerQty } }) => !!layerQty,
    transformAction: ({ result: { roundingRules = [], ...result }, source: { layerDown, layerQty, layerUp } }) => ({
      ...result,
      roundingRules: [
        ...roundingRules,
        { thresholdUp: layerDown ?? 0, value: layerQty ?? 0, thresholdDown: layerUp ?? 0 },
      ],
    }),
  },
  {
    condition: ({ source: { palletQty } }) => !!palletQty,
    transformAction: ({ result: { roundingRules = [], ...result }, source: { palletDown, palletQty, palletUp } }) => ({
      ...result,
      roundingRules: [
        ...roundingRules,
        { thresholdUp: palletDown ?? 0, value: palletQty ?? 0, thresholdDown: palletUp ?? 0 },
      ],
    }),
  },
]

export const castIProduct = (
  source: IProductCatalog,
  config?: IConfig,
  resetQuantity: boolean = true,
): IProduct => {
  const { bundlesExtra, jumpQty, layerQty, palletQty, layerDown, layerUp, palletDown, palletUp,...restSource } = source

  return productTransformers.reduce<IProduct>(
    (acc, { condition, transformAction }) => {
      const argsTransformers = { source, result: acc, config }
      return condition(argsTransformers)
        ? transformAction(argsTransformers)
        : acc
    },
    {
      ...restSource,
      subtotal: 0,
      quantity: resetQuantity ? 0 : restSource.quantity || 0,
      promotionalQty: 0,
      minQtyAllowed: 0,
      bundlesExtra: [],
      extraPackages: [],
    },
  )
}
export const castIProducts = (
  source: IProductCatalog[],
  config?: IConfig,
  resetQuantity: boolean = true,
) => source.map((product) => castIProduct(product, config, resetQuantity))

export const castICatalogItems = (
  source: ICatalogItemHeadless[],
  config?: IConfig,
): ICatalogItem[] =>
  source.map(({ productsPrices, parentId, ...item }) => ({
    ...item,
    parentId: parentId || undefined,
    productsPrices: castIProducts(productsPrices || [], config),
  }))
